'use client'

import React, { FC, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { useTheme } from 'next-themes'
import {
	loadBitmapLayout,
	peekBitmapLayout,
	BitmapNotFoundError,
	type BitmapLayout
} from './layout-cache'
import { acquireGl, drawGlFrame, releaseGl, ENTRY_TOTAL_MS } from './gl-renderer'
import { drawCanvas2dFrame } from './canvas2d-renderer'

export type BitmapStatus = 'idle' | 'loading' | 'done' | 'error' | 'missing'

export type BitmapCanvasProps = {
	height: number
	/** CSS size the canvas is designed for; the backing store is scaled by DPR. */
	canvasSize?: number
	renderer: 'webgl' | 'canvas2d'
	enableRepulsion?: boolean
	/** Start the entry animation only once the tile is on screen. */
	inView?: boolean
	/** Draw the finished bitmap immediately (returning tiles, reduced motion). */
	skipEntryAnimation?: boolean
	/** Replay the fly-in even when this block's layout is already cached. */
	replayEntryFromCache?: boolean
	/** Duration of the fly-in; defaults to the standard Bitmap timing. */
	entryDurationMs?: number
	/** Called once after the fly-in finishes. */
	onEntryComplete?: () => void
	/** Extrude the squares into isometric blocks (WebGL only); toggling animates. */
	isometric?: boolean
	maxDpr?: number
	mobileMode?: boolean
	onStatus?: (s: BitmapStatus) => void
	onLayout?: (l: BitmapLayout) => void
	/** Background to paint; defaults to the site theme. 'dark' keeps the canvas dark in light mode. */
	background?: 'theme' | 'dark'
}

type Mouse = { x: number; y: number } | null

/** A ref that always holds the latest value, for use inside long-lived loops. */
function useLatest<T>(value: T) {
	const ref = useRef(value)
	useLayoutEffect(() => {
		ref.current = value
	})
	return ref
}

/**
 * The bitmap itself. Fetches the block's transaction sizes, lays them out in a
 * worker and animates the squares flying in over three seconds. After that the
 * cursor (or a long press on touch) pushes squares around until it leaves.
 */
const BitmapCanvas: FC<BitmapCanvasProps> = ({
	height,
	canvasSize = 300,
	renderer,
	enableRepulsion = true,
	inView = true,
	skipEntryAnimation = false,
	replayEntryFromCache = false,
	entryDurationMs = ENTRY_TOTAL_MS,
	isometric = false,
	maxDpr,
	mobileMode = false,
	onStatus,
	onLayout,
	onEntryComplete,
	background = 'theme'
}) => {
	const { resolvedTheme } = useTheme()
	const lightBackground = background === 'theme' && resolvedTheme !== 'dark'
	const canvasRef = useRef<HTMLCanvasElement | null>(null)
	const ctxRef = useRef<CanvasRenderingContext2D | null>(null)
	const layoutRef = useRef<BitmapLayout | null>(null)
	const rafRef = useRef(0)
	const mouseRef = useRef<Mouse>(null)
	const animatingRef = useRef(false)
	const enteredRef = useRef(false)
	const inViewRef = useLatest(inView)
	const repulsionRef = useLatest(enableRepulsion)
	const skipRef = useLatest(skipEntryAnimation)
	const onStatusRef = useLatest(onStatus)
	const onLayoutRef = useLatest(onLayout)
	const onEntryCompleteRef = useLatest(onEntryComplete)
	const entryCompleteRef = useRef(false)
	const glRef = useRef<ReturnType<typeof acquireGl>>(null)
	// 0 = flat, 1 = isometric; eased toward the prop over ~1.8s.
	const tileHeightRef = useRef(isometric ? 1 : 0)
	const tileHeightRafRef = useRef(0)

	const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
	const size = Math.round(canvasSize * (maxDpr ? Math.min(dpr, maxDpr) : dpr))
	const sizeRef = useLatest(size)

	const draw = useCallback(
		(startTime: number, now: number, progress: number) => {
			const ctx = ctxRef.current
			const layout = layoutRef.current
			if (!ctx || !layout) return
			const mouse = repulsionRef.current ? mouseRef.current : null
			if (renderer === 'webgl' && glRef.current) {
				drawGlFrame(glRef.current, ctx, {
					size: sizeRef.current,
                    lightBackground,
					layout,
					startTime,
					now,
					mouse,
					repulsion: repulsionRef.current,
					tileHeight: tileHeightRef.current
				})
			} else {
				drawCanvas2dFrame(ctx, {
					size: sizeRef.current,
                    lightBackground,
					layout,
					progress,
					startTime,
					now,
					mouse,
					mobile: mobileMode
				})
			}
		},
		[renderer, mobileMode, repulsionRef, sizeRef, lightBackground]
	)

	/** Finished bitmap, no animation. */
	const drawStatic = useCallback(() => draw(0, ENTRY_TOTAL_MS + 1000, 1), [draw])

	/** Three-second fly-in, then keep running while the cursor is over the tile. */
	const runEntry = useCallback(() => {
		enteredRef.current = true
		const start = performance.now()
		animatingRef.current = true
		const frame = (now: number) => {
			if (!layoutRef.current || !ctxRef.current) {
				animatingRef.current = false
				return
			}
			const virtualNow = start + ((now - start) * ENTRY_TOTAL_MS) / entryDurationMs
			const progress = Math.min(1, (virtualNow - start) / ENTRY_TOTAL_MS)
			draw(start, virtualNow, progress)
			if (progress === 1 && !entryCompleteRef.current) {
				entryCompleteRef.current = true
				onEntryCompleteRef.current?.()
			}
			if (progress < 1 || mouseRef.current) {
				rafRef.current = requestAnimationFrame(frame)
			} else {
				animatingRef.current = false
			}
		}
		cancelAnimationFrame(rafRef.current)
		rafRef.current = requestAnimationFrame(frame)
	}, [draw, entryDurationMs, onEntryCompleteRef])

	/** Repulsion loop while the pointer is over a finished tile. */
	const runHover = useCallback(() => {
		if (animatingRef.current || !enteredRef.current || !repulsionRef.current) return
		animatingRef.current = true
		const frame = () => {
			if (!layoutRef.current || !ctxRef.current) {
				animatingRef.current = false
				return
			}
			drawStatic()
			if (mouseRef.current) rafRef.current = requestAnimationFrame(frame)
			else animatingRef.current = false
		}
		cancelAnimationFrame(rafRef.current)
		rafRef.current = requestAnimationFrame(frame)
	}, [drawStatic, repulsionRef])

	// Pointer tracking (mouse + long-press on touch), in backing-store pixels.
	useEffect(() => {
		const el = canvasRef.current
		if (!el) return
		const toCanvas = (cx: number, cy: number) => {
			const r = el.getBoundingClientRect()
			return { x: ((cx - r.left) * el.width) / r.width, y: ((cy - r.top) * el.height) / r.height }
		}
		const onMove = (e: MouseEvent) => {
			mouseRef.current = toCanvas(e.clientX, e.clientY)
		}
		const onEnter = () => runHover()
		const onLeave = () => {
			mouseRef.current = null
		}
		let pressTimer: ReturnType<typeof setTimeout> | null = null
		let pressing = false
		let startTouch = { x: 0, y: 0 }
		const onTouchStart = (e: TouchEvent) => {
			const t = e.touches[0]
			startTouch = { x: t.clientX, y: t.clientY }
			pressing = false
			pressTimer = setTimeout(() => {
				pressing = true
				mouseRef.current = toCanvas(t.clientX, t.clientY)
				runHover()
			}, 200)
		}
		const onTouchMove = (e: TouchEvent) => {
			const t = e.touches[0]
			if (
				!pressing &&
				Math.abs(t.clientX - startTouch.x) + Math.abs(t.clientY - startTouch.y) > 10 &&
				pressTimer
			) {
				clearTimeout(pressTimer)
				pressTimer = null
				return
			}
			if (pressing) mouseRef.current = toCanvas(t.clientX, t.clientY)
		}
		const onTouchEnd = () => {
			if (pressTimer) clearTimeout(pressTimer)
			pressTimer = null
			pressing = false
			mouseRef.current = null
		}
		el.addEventListener('mousemove', onMove)
		el.addEventListener('mouseenter', onEnter)
		el.addEventListener('mouseleave', onLeave)
		el.addEventListener('touchstart', onTouchStart, { passive: true })
		el.addEventListener('touchmove', onTouchMove, { passive: true })
		el.addEventListener('touchend', onTouchEnd, { passive: true })
		el.addEventListener('touchcancel', onTouchEnd, { passive: true })
		return () => {
			el.removeEventListener('mousemove', onMove)
			el.removeEventListener('mouseenter', onEnter)
			el.removeEventListener('mouseleave', onLeave)
			el.removeEventListener('touchstart', onTouchStart)
			el.removeEventListener('touchmove', onTouchMove)
			el.removeEventListener('touchend', onTouchEnd)
			el.removeEventListener('touchcancel', onTouchEnd)
			if (pressTimer) clearTimeout(pressTimer)
		}
	}, [runHover])

	// Renderer lifecycle + block load.
	useEffect(() => {
		const el = canvasRef.current
		if (!el) return
		el.width = size
		el.height = size
		const ctx = el.getContext('2d')
		if (!ctx) {
			onStatusRef.current?.('error')
			return
		}
		ctxRef.current = ctx
		if (renderer === 'webgl') {
			glRef.current = acquireGl(size)
			if (!glRef.current) {
				onStatusRef.current?.('error')
				return
			}
		}

		let cancelled = false
		enteredRef.current = false
		entryCompleteRef.current = false
		layoutRef.current = null
		const cached = peekBitmapLayout(height)
		if (!cached) onStatusRef.current?.('loading')

		const ready = (layout: BitmapLayout, fromCache: boolean) => {
			if (cancelled) return
			layoutRef.current = layout
			onLayoutRef.current?.(layout)
			onStatusRef.current?.('done')
			if (skipRef.current || (fromCache && !replayEntryFromCache) || !inViewRef.current) {
				// Already seen, cached, or off screen: paint the finished bitmap and
				// keep it. Wiping an already-painted tile to replay the fly-in as it
				// scrolled in was the most visible part of the clunky load.
				enteredRef.current = true
				drawStatic()
			} else {
				runEntry()
			}
		}

		if (cached) {
			ready(cached, true)
		} else {
			// Not aborted on cleanup: the request is shared by every tile showing
			// this block and its result is cached for the next mount.
			loadBitmapLayout(height)
				.then((layout) => ready(layout, false))
				.catch((e) => {
					if (cancelled) return
					if (!(e instanceof BitmapNotFoundError))
						console.warn(`[bitmap] block ${height} failed to load`, e)
					onStatusRef.current?.(e instanceof BitmapNotFoundError ? 'missing' : 'error')
				})
		}

		return () => {
			cancelled = true
			cancelAnimationFrame(rafRef.current)
			animatingRef.current = false
			if (glRef.current) {
				releaseGl()
				glRef.current = null
			}
			ctxRef.current = null
		}
	}, [height, size, renderer, drawStatic, runEntry, inViewRef, onLayoutRef, onStatusRef, skipRef, replayEntryFromCache])

	// 2D ⇄ 3D: ease the extrusion between 0 and 1, redrawing each frame
	// (unless the entry or hover loop is already redrawing).
	useEffect(() => {
		const target = isometric ? 1 : 0
		const from = tileHeightRef.current
		if (Math.abs(target - from) < 0.001) return
		cancelAnimationFrame(tileHeightRafRef.current)
		const distance = Math.abs(target - from)
		const duration = distance < 0.8 ? Math.max(300, Math.round(1800 * distance)) : 1800
		const start = performance.now()
		const frame = (now: number) => {
			const t = Math.min(1, (now - start) / duration)
			tileHeightRef.current = from + (target - from) * (1 - Math.pow(1 - t, 4))
			if (!animatingRef.current && layoutRef.current && ctxRef.current) drawStatic()
			if (t < 1) tileHeightRafRef.current = requestAnimationFrame(frame)
		}
		tileHeightRafRef.current = requestAnimationFrame(frame)
		return () => cancelAnimationFrame(tileHeightRafRef.current)
	}, [isometric, drawStatic])

	// A tile whose data is still loading when it scrolls into view plays the
	// fly-in once the data lands (see ready()); nothing to do here.
	useEffect(() => {
		if (inView && layoutRef.current && !enteredRef.current) runEntry()
	}, [inView, runEntry])

	return <canvas ref={canvasRef} className="block" style={{ width: '100%', height: '100%' }} />
}

export default BitmapCanvas
